import { ReactNode } from 'react'
import { ShieldCheck, Plus } from 'lucide-react'
import './AuthLayout.css'

interface AuthLayoutProps {
  children: ReactNode
}

/**
 * Immersive login "scene".
 *
 * A committed visual world for the auth screen: a multi-layer mesh gradient
 * canvas, floating blurred blobs + grain, an animated vitals focal point on
 * the brand side, and a floating glass access card offset over the seam for
 * intentional asymmetry (≈59/41 on desktop).
 *
 * Desktop: brand composition left, glass card right (overlapping the seam).
 * Mobile: single column — the gradient/blobs are preserved and a compact
 * brand header sits above the card.
 *
 * All decorative motion respects `prefers-reduced-motion` (see AuthLayout.css).
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="auth-scene">
      {/* Ambient background: blurred blobs + grain */}
      <div className="auth-bg" aria-hidden="true">
        <span className="auth-blob b1" />
        <span className="auth-blob b2" />
        <span className="auth-blob b3" />
        <div className="auth-grain" />
      </div>

      <div className="auth-grid">
        {/* Brand side (desktop) */}
        <section className="auth-brand">
          <VitalsFocal />

          <div className="auth-brand-copy">
            <h1 className="auth-wordmark">
              Medi<span className="accent">Lux</span>
            </h1>
            <p className="auth-brand-tagline">Sistema de gestión clínica</p>

            <p className="auth-brand-message">
              Gestión clínica que trabaja tan duro como tú: silenciosa, confiable
              y pensada para tu equipo.
            </p>

            <p className="auth-brand-footer">
              <ShieldCheck size={16} strokeWidth={1.75} />
              Conexión cifrada · Datos protegidos
            </p>
          </div>
        </section>

        {/* Form side */}
        <section className="auth-form-col">
          <MobileBrand />
          <div className="auth-card">{children}</div>
        </section>
      </div>
    </div>
  )
}

/**
 * Animated focal composition — evokes vitals: concentric pulse rings, a slowly
 * rotating conic "orb", two orbiting dots, and an EKG trace. Purely decorative.
 */
function VitalsFocal() {
  return (
    <div className="auth-focal" aria-hidden="true">
      <span className="auth-ring r1" />
      <span className="auth-ring r2" />
      <span className="auth-ring r3" />
      <span className="auth-orb" />
      <span className="auth-orbit o1">
        <span className="dot" />
      </span>
      <span className="auth-orbit o2">
        <span className="dot" />
      </span>
      <svg className="auth-ekg" viewBox="0 0 340 60" preserveAspectRatio="none">
        <path d="M0,30 H120 l10,-22 l12,44 l10,-30 l8,16 H200 l14,-14 l10,14 H340" />
      </svg>
    </div>
  )
}

/** Compact brand block shown above the card on mobile/tablet. */
function MobileBrand() {
  return (
    <div className="auth-mobile-brand">
      <span className="glyph">
        <Plus size={26} strokeWidth={2.5} color="#fff" />
      </span>
      <span className="name">MediLux</span>
      <span className="sub">Sistema de gestión clínica</span>
    </div>
  )
}
